import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme, { mobileBreakpoint } from '../theme';
import PageLayout from './PageLayout';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import { LessonStatus, InformationText, PrimaryButton, SecondaryButton, ManageButton, PaginationContainer, PaginationButton, PaginationInfo, AdminBadge, AcceptButton, MessageStrip, MessageIcon, MessageText, MessageLink } from './shared/CommonElements';
import ConfirmationPopup from './ConfirmationPopup';
import config from '../config';

const Container = styled.div`
  max-width: 800px;
  margin: 40px auto 0 auto;
  padding: 32px 16px 64px 16px;
  background: linear-gradient(135deg, #fff 0%, #f8f9ff 100%);
  border-radius: ${theme.borderRadius.large};
  box-shadow: 0 8px 32px rgba(0,0,0,0.08);
  overflow-x: hidden;
`;

const Title = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 15px;
  margin-top: -5px;
  word-break: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  max-width: 100%;
`;

const HeroSection = styled.section`
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin: 40px 0 0 0;
  margin-bottom: 16px;
`;

const HeroTitle = styled.div`
  display: flex;
  margin-bottom: 16px;
  align-items: center;
  
`;


const MainTitle = styled.h1`
  font-size: 64px;
  z-index: 1000;
  margin: 0;
  line-height: 1;
  color: ${props => props.theme.colors.primary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 48px;
  }
`;

const Subtitle = styled.h2`
  font-size: 32px;
  z-index: 1000;
  font-style: italic;
  margin: 0 0 24px 0;
  color: ${props => props.theme.colors.secondary};
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 24px;
  }
`;
const Description = styled.p`
  font-size: 18px;
  line-height: 1.6;
  margin-bottom: 32px;
  color: ${props => props.theme.colors.primary};
`;

const SectionTitle = styled.h2`
  font-size: 1.3rem;
  color: ${theme.colors.primary};
  font-weight: 600;
  margin: 32px 0 16px 0;
`;

const BudgetsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
  justify-content: center;
  margin-bottom: 18px;
`;

const BudgetTypeBox = styled.div`
  background: linear-gradient(135deg, #e3f6fd 0%, #f8e1f6 100%);
  border-radius: ${theme.borderRadius.medium};
  border: 0px solid ${theme.colors.accent2};
  min-width: 220px;
  padding: 18px 24px;
  text-align: center;
  box-shadow: 2px 2px 6px rgba(0,0,0,0.04);
  transition: all 0.3s ease;
  &:hover {
    transform: scale(1.025);
    box-shadow: 5px 5px 20px rgba(96, 80, 85, 0.11);
  }
  @media (max-width: ${mobileBreakpoint}px) {
    min-width: 250px;
  }
`;

const BudgetTypeLabel = styled.div`
  color: ${theme.colors.secondary};
  font-size: 1.08rem;
  font-weight: 600;
  margin-bottom: 6px;
`;

const BudgetTypeAmount = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: ${theme.colors.primary};
`;

const SkeletonBudgetTypeBox = styled.div`
  background: linear-gradient(135deg, #f0f0f0 0%, #e0e0e0 100%);
  border-radius: ${theme.borderRadius.medium};
  min-width: 220px;
  padding: 18px 24px;
  text-align: center;
  box-shadow: 2px 2px 6px rgba(0,0,0,0.04);
  animation: pulse 1.5s ease-in-out infinite;
  
  @keyframes pulse {
    0% { opacity: 0.6; }
    50% { opacity: 1; }
    100% { opacity: 0.6; }
  }
  
  @media (max-width: ${mobileBreakpoint}px) {
    min-width: 250px;
  }
`;

const SkeletonBudgetTypeLabel = styled.div`
  background: #d0d0d0;
  height: 20px;
  border-radius: 4px;
  margin-bottom: 6px;
  width: 80px;
  margin: 0 auto 6px auto;
`;

const SkeletonBudgetTypeAmount = styled.div`
  background: #d0d0d0;
  height: 32px;
  border-radius: 4px;
  width: 120px;
  margin: 0 auto;
`;

const LessonsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const LessonCard = styled.div`
  background: ${props => {
    switch (props.category) {
      case 'CHILDREN':
        return 'rgba(188, 231, 253, 0.13)';
      case 'ADULTS':
        return 'rgba(196, 146, 177, 0.08)';
      case 'SPECIAL':
        return 'rgba(219, 39, 98, 0.04)';
      default:
        return '#f5f5f5';
    }
  }};
  border: 1px solid ${props => {
    switch (props.category) {
      case 'CHILDREN':
        return '#bce7fd';
      case 'ADULTS':
        return '#c492b1';
      case 'SPECIAL':
        return '#db2762';
      default:
        return '#e0e0e0';
    }
  }};
  border-radius: ${theme.borderRadius.medium};
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  }
`;

const DangerButton = styled(AcceptButton)`
  background: #dc3545;
  &:hover {
    background: #c82333;
    box-shadow: 0 6px 20px rgba(200, 35, 51, 0.4);
  }
  @media (max-width: ${mobileBreakpoint}px) {
    width: 100%;
    min-width: unset;
  }
`;

const LessonTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${theme.colors.primary};
  margin: 0 0 12px 0;
`;

const LessonInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: ${theme.colors.primary};
  font-size: 1rem;
  
  span {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex-wrap: wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
    
    b {
      white-space: nowrap;
      flex-shrink: 0;
    }
  }
`;

const TemporaryUserContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 16px;
  @media (max-width: ${mobileBreakpoint}px) {
    flex-direction: column;
    justify-content: flex-start;
  }
`; 

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
  
  @media (max-width: ${mobileBreakpoint}px) {
    flex-direction: column;
  }
`;

const ManageButtonSecondary = styled(ManageButton)`
  background: #eee;
  color: ${theme.colors.primary};
  &:hover {
    background: #f5f5f5;
    color: ${theme.colors.secondary};
    box-shadow: 0 6px 20px rgba(193, 193, 193, 0.4);
  }
  @media (max-width: ${mobileBreakpoint}px) {
    width: 100%;
    min-width: unset;
  }
`;

const ManageButtonTemporary = styled(ManageButton)`
  @media (max-width: ${mobileBreakpoint}px) {
    width: 100%;
    min-width: unset;
  }
`;


const MeysPage = () => {
  const { isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  
  
  // Check token age
  const tokenTime = localStorage.getItem('token_time');
  if (tokenTime) {
    const age = Date.now() - parseInt(tokenTime, 10);
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours
  }
  
  // Add loading effect to handle AuthContext initialization
  useEffect(() => {
    setIsLoading(false);
  }, []);
  
  // Simplified authentication check for debugging
  const checkAuth = () => {
    const storedToken = localStorage.getItem('token');
    const storedTime = localStorage.getItem('token_time');
    
    // For now, just check if token exists and has some content
    if (!storedToken || storedToken.trim() === '') {
      return false;
    }
    
    return true;
  };
  
  // State for dynamic lessons data
  const [bookedLessons, setBookedLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(true);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const lessonsPerPage = 5;

  // Pagination logic
  const totalPages = Math.ceil(bookedLessons.length / lessonsPerPage);
  const startIndex = (currentPage - 1) * lessonsPerPage;
  const endIndex = startIndex + lessonsPerPage;
  const currentLessons = bookedLessons.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const [overviewFacts, setOverviewFacts] = useState({
    cards: [],
    period: "30 Tage"
  });
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [temporaryUsers, setTemporaryUsers] = useState([]);
  const [temporaryUsersLoading, setTemporaryUsersLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [lessonsWithoutSummaryCount, setLessonsWithoutSummaryCount] = useState(0);
  const [lessonsWithoutSummaryLoading, setLessonsWithoutSummaryLoading] = useState(true);

  // Temporary users pagination state
  const [currentTemporaryUsersPage, setCurrentTemporaryUsersPage] = useState(1);
  const temporaryUsersPerPage = 5;

  // Temporary users pagination logic
  const totalTemporaryUsersPages = Math.ceil(temporaryUsers.length / temporaryUsersPerPage);
  const startTemporaryUsersIndex = (currentTemporaryUsersPage - 1) * temporaryUsersPerPage;
  const endTemporaryUsersIndex = startTemporaryUsersIndex + temporaryUsersPerPage;
  const currentTemporaryUsers = temporaryUsers.slice(startTemporaryUsersIndex, endTemporaryUsersIndex);

  const handleTemporaryUsersPageChange = (page) => {
    setCurrentTemporaryUsersPage(page);
  };

  const handleTemporaryUsersPreviousPage = () => {
    if (currentTemporaryUsersPage > 1) {
      setCurrentTemporaryUsersPage(currentTemporaryUsersPage - 1);
    }
  };

  const handleTemporaryUsersNextPage = () => {
    if (currentTemporaryUsersPage < totalTemporaryUsersPages) {
      setCurrentTemporaryUsersPage(currentTemporaryUsersPage + 1);
    }
  };

  // Confirmation popup state
  const [approvalPopup, setApprovalPopup] = useState({
    isOpen: false,
    username: null
  });

  // Rejection popup state
  const [rejectionPopup, setRejectionPopup] = useState({
    isOpen: false,
    username: null,
    rejectionReason: ''
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch overview facts from backend
  useEffect(() => {
    const fetchOverviewFacts = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setOverviewLoading(false);
          return;
        }

        const response = await fetch(config.getApiUrl('/api/meyspage/overview-facts'), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setOverviewFacts(data);
        } else {
          console.error('Failed to fetch overview facts:', response.status);
        }
      } catch (error) {
        console.error('Error fetching overview facts:', error);
      } finally {
        setOverviewLoading(false);
      }
    };

    fetchOverviewFacts();
  }, []);

  // Fetch lessons from backend
  useEffect(() => {
    const fetchLessons = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLessonsLoading(false);
          return;
        }

        const response = await fetch(config.getApiUrl('/api/meyspage/lessons'), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setBookedLessons(data.lessons || []);
          setCurrentPage(1); // Reset to first page when new lessons are fetched
        } else {
          console.error('Failed to fetch lessons:', response.status);
        }
      } catch (error) {
        console.error('Error fetching lessons:', error);
      } finally {
        setLessonsLoading(false);
      }
    };

    fetchLessons();
  }, []);

  // Fetch temporary users from backend
  useEffect(() => {
    const fetchTemporaryUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setTemporaryUsersLoading(false);
          return;
        }

        const response = await fetch(config.getApiUrl('/api/meyspage/temporary-users'), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setTemporaryUsers(data.temporaryUsers || []);
          setCurrentTemporaryUsersPage(1); // Reset to first page when new users are fetched
        } else {
          console.error('Failed to fetch temporary users:', response.status);
        }
      } catch (error) {
        console.error('Error fetching temporary users:', error);
      } finally {
        setTemporaryUsersLoading(false);
      }
    };

    fetchTemporaryUsers();
  }, []);

  // Fetch count of lessons without summaries
  useEffect(() => {
    const fetchLessonsWithoutSummaryCount = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLessonsWithoutSummaryLoading(false);
          return;
        }

        const url = new URL(config.getApiUrl('/api/lessons/past-lessons'));
        url.searchParams.append('withoutSummary', 'true');
        url.searchParams.append('countOnly', 'true');

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setLessonsWithoutSummaryCount(data.count || 0);
        } else {
          console.error('Failed to fetch lessons without summary count:', response.status);
        }
      } catch (error) {
        console.error('Error fetching lessons without summary count:', error);
      } finally {
        setLessonsWithoutSummaryLoading(false);
      }
    };

    fetchLessonsWithoutSummaryCount();
  }, []);

  const formatGermanDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusName = (status) => {
    switch (status) {
      case 'CONFIRMED':
        return 'Bestätigt';
      case 'UNCONFIRMED':
        return 'Ausstehend';
      case 'CANCELLED':
        return 'Storniert';
      case 'DONE':
        return 'Abgeschlossen';
      case 'RESCHEDULE_REQUESTED':
        return 'Verschiebung angefragt';
      case 'SCHEDULING':
        return 'Geplant';
      default:
        return 'Unbekannt';
    }
  };

  const handleManageLesson = (lessonId) => {
    navigate(`/manage-lesson/${lessonId}`);
  };

  const handleViewAllLessons = () => {
    navigate('/past-lessons');
  };

  const handleViewLessonsWithoutSummary = () => {
    navigate('/past-lessons?withoutSummary=true');
  };

  const handleViewAllUsers = () => {
    navigate('/all-users');
  };

  const handleApproveUser = async (username) => {
    // Show confirmation popup first
    setApprovalPopup({
      isOpen: true,
      username: username
    });
  };

  const handleConfirmApproval = async () => {
    const username = approvalPopup.username;
    if (!username) return;

    try {
      // Update the user's approving status
      setTemporaryUsers(prev => prev.map(user => 
        user.username === username 
          ? { ...user, approving: true }
          : user
      ));

      const token = localStorage.getItem('token');
      const response = await fetch(config.getApiUrl(`/api/meyspage/temporary-users/${username}/approve`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Remove the approved user from the list
        setTemporaryUsers(prev => prev.filter(user => user.username !== username));
        
        // Reset pagination if we're now on a page with no users
        if (currentTemporaryUsersPage > 1 && currentTemporaryUsers.length === 1) {
          setCurrentTemporaryUsersPage(prev => Math.max(1, prev - 1));
        }
      } else {
        const errorData = await response.json();
        console.error('Failed to approve user:', errorData.error || 'Unknown error');
        
        // Show error message
        setNotification({
          type: 'error',
          message: `Fehler beim Genehmigen: ${errorData.error || 'Unbekannter Fehler'}`
        });
        
        // Clear notification after 5 seconds
        setTimeout(() => setNotification(null), 5000);
        
        // Reset the approving status on error
        setTemporaryUsers(prev => prev.map(user => 
          user.username === username 
            ? { ...user, approving: false }
            : user
        ));
      }
    } catch (error) {
      console.error('Error approving user:', error);
      
      // Show error message
      setNotification({
        type: 'error',
        message: 'Verbindungsfehler beim Genehmigen des Benutzers'
      });
      
      // Clear notification after 5 seconds
      setTimeout(() => setNotification(null), 5000);
      
      // Reset the approving status on error
      setTemporaryUsers(prev => prev.map(user => 
        user.username === username 
          ? { ...user, approving: false }
          : user
      ));
    } finally {
      // Close the popup
      setApprovalPopup({
        isOpen: false,
        username: null
      });
    }
  };

  const handleRejectUser = async (username) => {
    // Show rejection popup first
    setRejectionPopup({
      isOpen: true,
      username: username,
      rejectionReason: ''
    });
  };

  const handleConfirmRejection = async () => {
    const username = rejectionPopup.username;
    const rejectionReason = rejectionPopup.rejectionReason;
    
    if (!username) {
      return; // Don't proceed if no user provided
    }

    try {
      // Update the user's rejecting status
      setTemporaryUsers(prev => prev.map(user => 
        user.username === username 
          ? { ...user, rejecting: true }
          : user
      ));

      const token = localStorage.getItem('token');
      const response = await fetch(config.getApiUrl(`/api/meyspage/temporary-users/${username}/reject?rejectionReason=${encodeURIComponent(rejectionReason)}`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Remove the rejected user from the list
        setTemporaryUsers(prev => prev.filter(user => user.username !== username));
        
        // Reset pagination if we're now on a page with no users
        if (currentTemporaryUsersPage > 1 && currentTemporaryUsers.length === 1) {
          setCurrentTemporaryUsersPage(prev => Math.max(1, prev - 1));
        }
      } else {
        const errorData = await response.json();
        console.error('Failed to reject user:', errorData.error || 'Unknown error');
        
        // Show error message
        setNotification({
          type: 'error',
          message: `Fehler beim Ablehnen: ${errorData.error || 'Unbekannter Fehler'}`
        });
        
        // Clear notification after 5 seconds
        setTimeout(() => setNotification(null), 5000);
        
        // Reset the rejecting status on error
        setTemporaryUsers(prev => prev.map(user => 
          user.username === username 
            ? { ...user, rejecting: false }
            : user
        ));
      }
    } catch (error) {
      console.error('Error rejecting user:', error);
      
      // Show error message
      setNotification({
        type: 'error',
        message: 'Verbindungsfehler beim Ablehnen des Benutzers'
      });
      
      // Clear notification after 5 seconds
      setTimeout(() => setNotification(null), 5000);
      
      // Reset the rejecting status on error
      setTemporaryUsers(prev => prev.map(user => 
        user.username === username 
          ? { ...user, rejecting: false }
          : user
      ));
    } finally {
      // Close the popup
      setRejectionPopup({
        isOpen: false,
        username: null,
        rejectionReason: ''
      });
    }
  };

  if (isLoading) {
    return (
      <PageLayout>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <h2>Lade Admin-Bereich...</h2>
        </div>
      </PageLayout>
    );
  }
  
  if (!checkAuth()) {
    return (
      <PageLayout>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <h2>Bitte melde dich an, um auf den Admin-Bereich zuzugreifen.</h2>
          <div style={{ marginTop: '20px' }}>
            <button 
              onClick={() => {
              }}
              style={{
                padding: '10px 20px',
                background: theme.colors.secondary,
                color: 'white',
                border: 'none',
                borderRadius: theme.borderRadius.medium,
                cursor: 'pointer'
              }}
            >
              Debug: Auth Status prüfen
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout style={{ overflowX: 'hidden' }}>
      <HeroSection>
        <HeroTitle>
          <AdminBadge>
            <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', margin: 'auto', transform: 'translateY(5px)' }}>
              <path d="M12 8L15 13.2L18 10.5L17.3 14H6.7L6 10.5L9 13.2L12 8M12 2L8.5 11L3 5L5 16H19L21 5L15.5 11L12 2Z"/>
            </svg>
          </AdminBadge>
          <MainTitle>Meys Bereich</MainTitle>
        </HeroTitle>
        <Subtitle>Admin Dashboard</Subtitle>
        <Description>
          Willkommen im Admin-Bereich. Hier kannst du alle gebuchten Stunden, 
          Probestunden und Benutzeranfragen verwalten.
        </Description>
      </HeroSection>

              <Container>
          <Title>Übersicht</Title>
          {overviewLoading ? (
            <BudgetsGrid>
              <SkeletonBudgetTypeBox>
                <SkeletonBudgetTypeLabel />
                <SkeletonBudgetTypeAmount />
              </SkeletonBudgetTypeBox>
              <SkeletonBudgetTypeBox>
                <SkeletonBudgetTypeLabel />
                <SkeletonBudgetTypeAmount />
              </SkeletonBudgetTypeBox>
            </BudgetsGrid>
          ) : (
            <>
              <BudgetsGrid>
                {overviewFacts.cards && overviewFacts.cards.length > 0 ? (
                  overviewFacts.cards.map((card, index) => (
                    <BudgetTypeBox key={index}>
                      <BudgetTypeLabel>{card.title}</BudgetTypeLabel>
                      <BudgetTypeAmount>{card.value}</BudgetTypeAmount>
                    </BudgetTypeBox>
                  ))
                ) : (
                  <div style={{textAlign: 'center', padding: '20px', color: '#888', gridColumn: '1 / -1'}}>
                    Keine Übersichtsdaten verfügbar
                  </div>
                )}
              </BudgetsGrid>
              <InformationText style={{marginBottom: '20px', marginTop: '30px'}}>
                Hier siehst du die wichtigsten aktuellen Statistiken für die letzten {overviewFacts.period}.
              </InformationText>
              <div style={{ display: 'flex', gap: '0px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '-20px' }}>
                <SecondaryButton onClick={() => navigate('/purchased-packages')}>
                  Gekaufte Pakete anzeigen
                </SecondaryButton>
              </div>
            </>
          )}
        </Container>

      <Container>
        <Title>Alle gebuchten Stunden</Title>
        
        {/* Information message strip for lessons without summaries */}
        {!lessonsWithoutSummaryLoading && lessonsWithoutSummaryCount > 0 && (
          <MessageStrip variant="info" compact>
            <MessageIcon>📝</MessageIcon>
            <MessageText>
              {lessonsWithoutSummaryCount === 1 
                ? 'Es gibt 1 vergangene Stunde ohne Zusammenfassung.' 
                : `Es gibt ${lessonsWithoutSummaryCount} vergangene Stunden ohne Zusammenfassung.`
              }
              {' '}
              <MessageLink onClick={handleViewLessonsWithoutSummary}>
                Anzeigen →
              </MessageLink>
            </MessageText>
          </MessageStrip>
        )}
        
        {lessonsLoading ? (
          <div style={{textAlign: 'center', padding: '40px', color: '#888'}}>
            Lade Stunden...
          </div>
        ) : bookedLessons.length > 0 ? (
          <>
            <LessonsList>
              {currentLessons.map(lesson => (
                <LessonCard key={lesson.id} category={lesson.category}>
                  <LessonTitle>{lesson.topic}</LessonTitle>
                  <LessonInfo>
                    <span><b>Datum:</b> {formatGermanDate(lesson.date)}</span>
                    <span><b>Zeit:</b> {lesson.time} Uhr</span>
                    <span><b>Schüler:</b> {lesson.studentName}</span>
                    <span><b>Email:</b> {lesson.studentEmail}</span>
                    <span><b>Status:</b> <LessonStatus status={getStatusName(lesson.status)}>{getStatusName(lesson.status)}</LessonStatus></span>
                  </LessonInfo>
                  <ManageButton onClick={() => handleManageLesson(lesson.id)}>
                    Stunde verwalten
                  </ManageButton>
                </LessonCard>
              ))}
            </LessonsList>
            
            {totalPages > 1 && (
              <PaginationContainer>
                <PaginationButton 
                  onClick={handlePreviousPage} 
                  disabled={currentPage === 1}
                >
                  ←
                </PaginationButton>
                
                {Array.from({ length: totalPages }, (_, index) => index + 1).map(page => (
                  <PaginationButton
                    key={page}
                    onClick={() => handlePageChange(page)}
                    active={currentPage === page}
                  >
                    {page}
                  </PaginationButton>
                ))}
                
                <PaginationButton 
                  onClick={handleNextPage} 
                  disabled={currentPage === totalPages}
                >
                  →
                </PaginationButton>
              </PaginationContainer>
            )}
          </>
        ) : (
          <div style={{textAlign: 'center', padding: '40px', color: '#888'}}>
            Keine Stunden gefunden.
          </div>
        )}
        
        <div style={{ display: 'flex', gap: '0px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '-20px', marginTop: '20px' }}>
          <SecondaryButton onClick={handleViewAllLessons}>
            Vergangene Stunden
          </SecondaryButton>
        </div>
      </Container>

      <Container>
        <Title>Benutzerverwaltung</Title>
        <InformationText style={{marginBottom: '20px'}}>
          Verwalte Benutzerkonten und neue Registrierungsanfragen.
        </InformationText>
        
        {notification && (
          <div style={{
            background: notification.type === 'success' ? '#e8f5e8' : '#ffebee',
            color: notification.type === 'success' ? '#2e7d32' : '#c62828',
            border: `1px solid ${notification.type === 'success' ? '#c8e6c9' : '#ffcdd2'}`,
            borderRadius: theme.borderRadius.medium,
            padding: '16px',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                color: 'inherit',
                padding: '0',
                marginLeft: '12px'
              }}
            >
              ×
            </button>
          </div>
        )}
        
        {temporaryUsersLoading ? (
          <div style={{textAlign: 'center', padding: '40px', color: '#888'}}>
            Lade temporäre Benutzer...
          </div>
                  ) : temporaryUsers.length > 0 ? (
            <>
              {currentTemporaryUsers.map((user, index) => (
                <LessonCard key={index} category="TRIAL" style={{ marginBottom: '18px' }}>
                  <LessonTitle>{user.firstName} ({user.username})</LessonTitle>
                  <LessonInfo>
                    <span><b>Info:</b> {user.additionalInfo}</span>
                    <span><b>Email:</b> {user.email}</span>
                    <span><b>Status:</b> <LessonStatus status='UNCONFIRMED'>{getStatusName('UNCONFIRMED')}</LessonStatus></span>
                  </LessonInfo>
                  <TemporaryUserContainer>
                    <ManageButtonTemporary
                      onClick={() => handleRejectUser(user.username)}
                      disabled={user.approving || user.rejecting}
                      style={{
                        minWidth: '160px'
                      }}
                    >
                      {user.rejecting ? 'Lehne ab...' : 'Ablehnen'}
                    </ManageButtonTemporary>
                    <AcceptButton
                      onClick={() => handleApproveUser(user.username)}
                      disabled={user.approving || user.rejecting}
                      style={{minWidth: '160px'}}
                    >
                      {user.approving ? 'Genehmige...' : 'Genehmigen'}
                    </AcceptButton>
                  </TemporaryUserContainer>
                </LessonCard>
              ))}
              {totalTemporaryUsersPages > 1 && (
                <PaginationContainer>
                  <PaginationButton 
                    onClick={handleTemporaryUsersPreviousPage} 
                    disabled={currentTemporaryUsersPage === 1}
                  >
                    ←
                  </PaginationButton>
                  
                  {Array.from({ length: totalTemporaryUsersPages }, (_, index) => index + 1).map(page => (
                    <PaginationButton
                      key={page}
                      onClick={() => handleTemporaryUsersPageChange(page)}
                      active={currentTemporaryUsersPage === page}
                    >
                      {page}
                    </PaginationButton>
                  ))}
                  
                  <PaginationButton 
                    onClick={handleTemporaryUsersNextPage} 
                    disabled={currentTemporaryUsersPage === totalTemporaryUsersPages}
                  >
                    →
                  </PaginationButton>
                </PaginationContainer>
              )}
            </>
        ) : (
          <div style={{ 
            background: '#f5f5f5', 
            border: '1px solid #e0e0e0', 
            borderRadius: theme.borderRadius.medium,
            padding: '24px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <h3 style={{margin: '0 0 16px 0', color: theme.colors.secondary}}>✅ Keine temporären Benutzer</h3>
            <p style={{margin: '0 0 16px 0', fontSize: '1rem', color: theme.colors.primary}}>
              Alle Benutzerregistrierungen wurden bereits bearbeitet.
            </p>
          </div>
        )}
        
        <SecondaryButton style={{marginBottom: '-20px', marginTop: '50px'}} onClick={handleViewAllUsers}>
              Benutzer verwalten
        </SecondaryButton>
      </Container>
      
      {/* Confirmation Popup for User Approval */}
      <ConfirmationPopup
        isOpen={approvalPopup.isOpen}
        onClose={() => setApprovalPopup({ isOpen: false, username: null })}
        onConfirm={handleConfirmApproval}
        title="Benutzer genehmigen"
        message={`Möchtest du den Benutzer "${approvalPopup.username}" wirklich genehmigen?`}
        confirmText="Genehmigen"
        cancelText="Abbrechen"
        isLoading={false}
      />

      {/* Rejection Popup */}
      <ConfirmationPopup
        isOpen={rejectionPopup.isOpen}
        onClose={() => setRejectionPopup({ isOpen: false, username: null, rejectionReason: '' })}
        onConfirm={handleConfirmRejection}
        title="Benutzer ablehnen"
        message={`Möchtest du den Benutzer "${rejectionPopup.username}" wirklich ablehnen?`}
        confirmText="Ablehnen"
        cancelText="Abbrechen"
        isLoading={false}
        showTextArea={true}
        isDestructive={true}
        prefilledText={rejectionPopup.rejectionReason}
        onTextChange={(text) => setRejectionPopup(prev => ({ ...prev, rejectionReason: text }))}
        placeholder="Ablehnungsgrund. Gibst du keinen Grund an, wird der Benutzer nicht informiert."
      />
    </PageLayout>
  );
};

export default MeysPage;
